// PhoneInput.tsx
import React, {
  useState,
  useRef,
  useLayoutEffect,
  KeyboardEvent,
  ClipboardEvent,
  MouseEvent,
} from 'react';

/* ------------------- Константы ------------------- */
const PREFIX = '+7 ';            // статический префикс, не входит в буфер
const MAX_DIGITS = 10;           // максимальное количество цифр без кода страны

/* ------------------- Утилиты ------------------- */

/**
 * Форматирует только «основную» часть номера (без +7).
 * Пример: 1234567890 → "(123) 456 78 90"
 */
const formatRest = (digits: string) => {
  const d = digits.padEnd(MAX_DIGITS, '_');
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8, 10)}`;
};

/**
 * Переводит позицию caret (полный текст, включая префикс) → логический индекс
 * (сколько цифр/placeholder‑ов слева от caret).
 */
const caretPosToDigitIndex = (formattedRest: string, visualCaret: number) => {
  const offset = PREFIX.length;
  const caretInRest = Math.max(0, visualCaret - offset);
  let cnt = 0;
  for (let i = 0; i < caretInRest; i++) {
    if (/[0-9_]/.test(formattedRest[i])) cnt++;
  }
  return cnt;
};

/**
 * Переводит логический индекс → позицию caret в полном тексте (с префиксом).
 */
const digitIndexToCaretPos = (formattedRest: string, digitIdx: number) => {
  let seen = 0;
  for (let i = 0; i < formattedRest.length; i++) {
    if (/[0-9_]/.test(formattedRest[i])) seen++;
    if (seen === digitIdx) return PREFIX.length + i + 1; // caret после символа
  }
  // Если курсор за пределами введённых цифр – ставим в конец строки
  return PREFIX.length + formattedRest.length;
};

/* ------------------- Компонент ------------------- */

type Props = {
  /** Чистый буфер (только цифры, без кода страны) */
  value?: string;
  /** Callback каждый раз, когда буфер меняется */
  onChange?: (rawDigits: string) => void;
  /** Блокировать ввод (например, во время отправки) */
  disabled?: boolean;
  /** CSS‑класс */
  className?: string;
  /** Плейсхолдер (можно переопределить) */
  placeholder?: string;
};













const PhoneInput: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  className,
  placeholder = 'Введите телефон',
}) => {
  /* ---------- Состояния ---------- */
  const isControlled = typeof value === 'string';
  const [internalDigits, setInternalDigits] = useState<string>(''); // 10‑digit buffer
  const digits = isControlled ? value! : internalDigits;

  // Логический курсор – количество цифр/placeholder‑ов слева от caret
  const [cursor, setCursor] = useState<number>(0);

  const inputRef = useRef<HTMLInputElement>(null);

  /* ---------- Синхронизация caret ---------------- */
  useLayoutEffect(() => {
    const formattedRest = formatRest(digits);
    const visualPos = digitIndexToCaretPos(formattedRest, cursor);
    if (inputRef.current) {
      inputRef.current.setSelectionRange(visualPos, visualPos);
    }
  }, [digits, cursor]);

  /* ---------- Обработчики клавиш --------------- */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const formattedRest = formatRest(digits);
    const input = inputRef.current!;
    const visualCaret = input.selectionStart ?? 0;
    const visualAnchor = input.selectionEnd ?? 0; // для выделения

    // --------------------- Стрелки -----------------
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret);
      setCursor(Math.max(0, logicalIdx - 1));
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret);
      setCursor(Math.min(digits.length, logicalIdx + 1));
      return;
    }

    // --------------------- Home / End -----------------
    if (e.key === 'Home') {
      e.preventDefault();
      setCursor(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setCursor(digits.length);
      return;
    }

    // --------------------- Цифры (режим замены) -----------------
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const newChar = e.key;

      // 1️⃣ Выделен диапазон → заменяем его одной цифрой
      if (visualCaret !== visualAnchor) {
        const startIdx = caretPosToDigitIndex(formattedRest, visualCaret);
        const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor);
        let newDigits = digits.slice(0, startIdx) + newChar + digits.slice(endIdx);
        // Обрезаем до 10 символов
        if (newDigits.length > MAX_DIGITS) newDigits = newDigits.slice(0, MAX_DIGITS);
        const newCursor = Math.min(startIdx + 1, MAX_DIGITS);

        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(newCursor);
        return;
      }

      // 2️⃣ Нет выделения – курсор где‑то внутри строки
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret);

      let newDigits: string;
      let newCursor: number;

      if (logicalIdx < digits.length) {
        // заменяем текущий символ
        newDigits = digits.slice(0, logicalIdx) + newChar + digits.slice(logicalIdx + 1);
        newCursor = logicalIdx + 1;
      } else {
        // вставляем в конец (только если ещё есть место)
        if (digits.length < MAX_DIGITS) {
          newDigits = digits + newChar;
          newCursor = digits.length + 1;
        } else {
          // буфер уже полон – ничего не меняем
          newDigits = digits;
          newCursor = digits.length;
        }
      }

      // гарантируем, что не превысили лимит
      if (newDigits.length > MAX_DIGITS) newDigits = newDigits.slice(0, MAX_DIGITS);
      newCursor = Math.min(newCursor, MAX_DIGITS);

      if (!isControlled) setInternalDigits(newDigits);
      onChange?.(newDigits);
      setCursor(newCursor);
      return;
    }

    // --------------------- Backspace / Delete -----------------
    if (e.key === 'Backspace') {
      e.preventDefault();
      // Если есть выделение – удаляем диапазон
      if (visualCaret !== visualAnchor) {
        const startIdx = caretPosToDigitIndex(formattedRest, visualCaret);
        const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor);
        const newDigits = digits.slice(0, startIdx) + digits.slice(endIdx);
        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(startIdx);
        return;
      }

      // Нет выделения – удаляем символ слева от курсора
      if (cursor === 0) return; // нельзя удалить префикс
      const newDigits = digits.slice(0, cursor - 1) + digits.slice(cursor);
      if (!isControlled) setInternalDigits(newDigits);
      onChange?.(newDigits);
      setCursor(cursor - 1);
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      // Выделение → просто удаляем диапазон (аналогично Backspace)
      if (visualCaret !== visualAnchor) {
        const startIdx = caretPosToDigitIndex(formattedRest, visualCaret);
        const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor);
        const newDigits = digits.slice(0, startIdx) + digits.slice(endIdx);
        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(startIdx);
        return;
      }

      // Нет выделения – заменяем текущий символ на placeholder
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret);
      if (logicalIdx < digits.length) {
        const newDigits = digits.slice(0, logicalIdx) + '_' + digits.slice(logicalIdx + 1);
        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(logicalIdx); // caret остаётся на месте
      }
      return;
    }
  };

  /* ------------------- Паста (режим замены) ------------------- */
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const raw = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (!raw) return;

    const input = inputRef.current!;
    const visualCaret = input.selectionStart ?? PREFIX.length;
    const visualAnchor = input.selectionEnd ?? visualCaret; // может быть выделено

    const formattedRest = formatRest(digits);
    const startIdx = caretPosToDigitIndex(formattedRest, visualCaret);
    const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor);

    // Если выделен диапазон – заменяем его
    let newDigits: string;
    let newCursor: number;

    if (visualCaret !== visualAnchor) {
      newDigits = digits.slice(0, startIdx) + raw + digits.slice(endIdx);
      newCursor = startIdx + raw.length;
    } else {
      // без выделения – вставляем/заменяем начиная с позиции курсора
      if (startIdx < digits.length) {
        // заменяем от startIdx, при этом «перезаписываем» существующие символы
        const left = digits.slice(0, startIdx);
        const right = digits.slice(startIdx + raw.length);
        newDigits = left + raw + right;
        newCursor = startIdx + raw.length;
      } else {
        // в конец (только если место есть)
        const free = MAX_DIGITS - digits.length;
        const toInsert = raw.slice(0, free);
        newDigits = digits + toInsert;
        newCursor = digits.length + toInsert.length;
      }
    }

    // Обрезаем до 10 цифр (важно, если вставка была длиннее)
    if (newDigits.length > MAX_DIGITS) newDigits = newDigits.slice(0, MAX_DIGITS);
    newCursor = Math.min(newCursor, MAX_DIGITS);

    if (!isControlled) setInternalDigits(newDigits);
    onChange?.(newDigits);
    setCursor(newCursor);
  };

  /* ------------------- Клик ------------------- */
  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    const visualPos = (e.target as HTMLInputElement).selectionStart ?? PREFIX.length;
    const formattedRest = formatRest(digits);
    const logicalIdx = caretPosToDigitIndex(formattedRest, visualPos);
    setCursor(logicalIdx);
  };

  const handleBlur = () => {
    // При необходимости можно добавить валидацию или сброс выделения
  };

  /* ------------------- Render ------------------- */
  return (
    <input
      ref={inputRef}
      type="tel"
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      // Показать префикс + отформатированную часть
      value={
        value !== ''?
         PREFIX + formatRest(digits)
         :
         value
        }
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onClick={handleClick}
      onBlur={handleBlur}
      inputMode="numeric"
      autoComplete="tel"
    />
  );
};

export default PhoneInput;
